import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../shared/Icon';
import { apiService } from '../../../services/api';
import { isThreadShared } from './sharedQueries';
import './SharePopover.css';

const GAP = 8;

/**
 * Share popover for a Saved queries row (PRD-1571 R2, Frame 1b).
 *
 * Portaled and fixed against the row like RowActionsMenu, opening to the
 * row's right. Not yet shared: the switch starts on and Share confirms; a
 * failure stays in the popover with the server's message and nothing changes.
 * Already shared ("Sharing settings"): turning the switch off hands over to
 * `onStopSharing`, which owns the Frame 4 confirmation.
 *
 * Dataset names come from the row's `pinned_dataset_ids`. Until every name has
 * loaded (or if any lookup fails) the datasets line stays generic, so it never
 * names only some of them.
 */
function SharePopover({ thread, anchorEl, onShare, onStopSharing, onClose }) {
  const shared = isThreadShared(thread);
  const [on, setOn] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [style, setStyle] = useState(null);
  const [datasetNames, setDatasetNames] = useState([]);
  const popRef = useRef(null);

  const datasetIdsKey = (thread.pinned_dataset_ids || []).join(',');
  useEffect(() => {
    const ids = datasetIdsKey ? datasetIdsKey.split(',') : [];
    if (ids.length === 0) return undefined;
    let cancelled = false;
    Promise.all(ids.map((id) => apiService.getDataset(id)))
      .then((datasets) => {
        const names = datasets.map((d) => d?.name).filter(Boolean);
        if (!cancelled && names.length === ids.length) setDatasetNames(names);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [datasetIdsKey]);

  useLayoutEffect(() => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    setStyle({
      position: 'fixed',
      left: rect.right + GAP,
      top: Math.max(GAP, Math.min(rect.top, window.innerHeight - (popRef.current?.offsetHeight || 0) - GAP)),
    });
  }, [anchorEl]);

  // `restoreFocus` is false when the user clicked or scrolled elsewhere: focus
  // belongs where they went, not back on the row.
  const close = useCallback((restoreFocus = true) => {
    if (!busy) onClose({ restoreFocus });
  }, [busy, onClose]);

  // Focus moves into the popover on open, like NotificationsPopover; the host
  // returns it to the row's trigger on close.
  useEffect(() => {
    popRef.current?.querySelector('.resui-switch-input')?.focus({ preventScroll: true });
  }, []);

  // Same contract as RowActionsMenu: outside click closes, Escape is owned here
  // (capture + stop) so a host listening on document doesn't also react, and any
  // layout shift invalidates the fixed position.
  useEffect(() => {
    const onDocClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) close(false);
    };
    const onShift = () => close(false);
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onShift);
    window.addEventListener('scroll', onShift, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onShift);
      window.removeEventListener('scroll', onShift, true);
    };
  }, [close]);

  const handleSwitch = (e) => {
    const next = e.target.checked;
    if (shared && !next) {
      onStopSharing(thread);
      return;
    }
    setOn(next);
  };

  const handleShare = async () => {
    setBusy(true);
    setError(null);
    try {
      await onShare(thread);
    } catch (err) {
      setError(err?.message || 'Failed to share this query.');
      setBusy(false);
    }
  };

  const title = thread.title || 'Untitled conversation';
  const datasetLine = datasetNames.length > 0
    ? `Uses ${datasetNames.join(', ')}. People without access to ${datasetNames.length === 1 ? 'it' : 'them'} will see the query name but cannot open it.`
    : 'People without access to its datasets will see the query name but cannot open it.';

  return createPortal(
    <div
      ref={popRef}
      className="share-popover"
      style={style || { position: 'fixed', visibility: 'hidden' }}
      role="dialog"
      aria-label={`Share "${title}"`}
      onClick={(e) => e.stopPropagation()}
    >
      <h2 className="share-popover-title">Share &ldquo;{title}&rdquo;</h2>
      <div className="share-popover-toggle">
        <div>
          <div className="share-popover-toggle-label">Share with your organization</div>
          <div className="share-popover-toggle-help">
            Anyone in your organization can view this conversation and ask in it.
          </div>
        </div>
        <label className="resui-switch">
          <input
            type="checkbox"
            className="resui-switch-input"
            checked={shared || on}
            onChange={handleSwitch}
            disabled={busy}
            aria-label="Share with your organization"
          />
          <span className="resui-switch-track"><span className="resui-switch-thumb" /></span>
        </label>
      </div>
      <ul className="share-popover-points">
        <li>Answers stay as first given. People with less access see the question, not the answer.</li>
        <li>{datasetLine}</li>
        <li>Only you can edit, stop sharing or delete.</li>
      </ul>
      {error && (
        <div className="resui-banner resui-banner--error share-popover-error" role="alert">
          <Icon name="error_outline" />
          <span className="resui-banner-text">{error}</span>
        </div>
      )}
      <div className="share-popover-actions">
        <button type="button" className="resui-btn resui-btn--secondary resui-btn--small" onClick={() => close()} disabled={busy}>
          <span className="resui-btn-label">{shared ? 'Close' : 'Cancel'}</span>
        </button>
        {!shared && (
          <button
            type="button"
            className="resui-btn resui-btn--primary resui-btn--small"
            onClick={handleShare}
            disabled={busy || !on}
          >
            <span className="resui-btn-label">{busy ? 'Working…' : 'Share'}</span>
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default SharePopover;
